import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useTripDocument } from "../../../../context/TripDocumentContext";
import {
  Box,
  Button,
  TextField,
  Typography,
  MenuItem,
  Select,
  FormControl,
  SelectChangeEvent,
} from "@mui/material";
import { SendTripDocumentRequest } from "../../../../types/api";
import { PODPayloadBufferIds } from "../../docPayloads";
import { useTripContext } from "../../../../context/tripsContext";
import { useAuth } from "../../../../utils/hooks/useAuth";
import { toast } from "react-toastify";
import { docSide } from "../../style";
import { useLocation, useParams } from "react-router-dom";
import { appInsights, trackFullPayload } from "../../../../utils/appInsights";
import { useUserFlowTracking } from "../../../../utils/hooks/useUserFlowTracking";
import { useDocumentNavigationData } from "../../../../utils/hooks/useDocumentNavigationData";
import { useNavigationContext } from "../../../../context/navigationContext";
import { RelatedLoad } from "../../../../types/api";

interface GridDocumentData {
  loadingPoint: string;
  unloadingPoint: string;
  loadingTmsLocation: string;
  unloadingTmsLocation: string;
  transportOrderTMSKey: string;
  involvedPartyName: string;
  invPartyName: string;
  tmsObjectNumber: string;
}

interface ISidebarGridProps {
  isOpen: boolean;
  callbackCloseSidebar: (shouldFetchDocuments: boolean) => void;
  callbackUpdateDocuments?: (documents: any) => void;
  action: boolean;
  onDocumentUploaded?: () => void;
  // Dati specifici del documento della grid
  gridDocumentData?: GridDocumentData;
}

const SidebarAddDocumentGrid: React.FC<ISidebarGridProps> = ({
  isOpen = false,
  callbackCloseSidebar,
  callbackUpdateDocuments,
  action,
  onDocumentUploaded,
  gridDocumentData
}) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const params = useParams();
  
  // Estrai il tripId dal pathname
  const tripIdFromUrl = currentPath.match(/\/tripdetails\/(\d+)/)?.[1];

  const [docName, setDocName] = useState<string>("");
  const [timestamp, setTimestamp] = useState<string>("");

  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const { uploadPOD, isDocumentLoading, sendTripDocument, errorMessage } =
    useTripDocument();

  const { tripDetails } = useTripContext();
  const { user } = useAuth();
  const { trackDocumentUploadFlow, trackInteraction } = useUserFlowTracking();
  const { getNavigationDataAsString } = useDocumentNavigationData();
  const { navigationSteps, sessionId } = useNavigationContext();

  // Per la grid, usiamo i dati dal documento selezionato
  // Il loading point è fisso e non modificabile

  // Aggiorna il valore selezionato quando cambia gridDocumentData o quando la sidebar si apre
  useEffect(() => {
    // Log unico per il debug della sidebar grid
    console.log('[DEBUG GRID] SidebarAddDocumentGrid State:', {
      // Props e stato
      isOpen,
      action,
      gridDocumentData,
      
      // Dati del documento selezionato
      documentInfo: gridDocumentData ? {
        loadingPoint: gridDocumentData.loadingPoint,
        unloadingPoint: gridDocumentData.unloadingPoint,
        loadingTmsLocation: gridDocumentData.loadingTmsLocation,
        unloadingTmsLocation: gridDocumentData.unloadingTmsLocation,
        transportOrderTMSKey: gridDocumentData.transportOrderTMSKey,
        involvedPartyName: gridDocumentData.involvedPartyName,
        invPartyName: gridDocumentData.invPartyName,
        tmsObjectNumber: gridDocumentData.tmsObjectNumber
      } : null,
      
      // Stringa concatenata attesa
      expectedConcatenatedString: gridDocumentData ? 
        `${gridDocumentData.loadingTmsLocation}_${gridDocumentData.unloadingTmsLocation}` : null,
      
      // Contesto
      hasGridDocumentData: !!gridDocumentData,
      source: 'grid_sidebar_useEffect'
    });
  }, [isOpen, gridDocumentData]);

  // Funzioni helper per formattare i dati
  const formatDuration = (milliseconds: number): string => {
    if (milliseconds < 1000) return `${milliseconds}ms`;
    if (milliseconds < 60000) return `${Math.round(milliseconds / 1000)}s`;
    const minutes = Math.floor(milliseconds / 60000);
    const seconds = Math.round((milliseconds % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  const getPageName = (path: string): string => {
    const pathMap: { [key: string]: string } = {
      '/dashboard': 'Dashboard',
      '/services': 'Servizi',
      '/documents': 'Documenti',
      '/contracts': 'Contratti',
      '/trips': 'Viaggi',
      '/fleet-and-route': 'Flotta e Percorsi',
      '/settings': 'Impostazioni',
      '/profile': 'Profilo',
      '/trip': 'Dettagli Viaggio',
      '/trip-documents': 'Documenti Viaggio',
      '/error-page': 'Pagina Errore'
    };
    
    return pathMap[path] || path;
  };

  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString('it-IT', { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit' 
    });
  };

  // Funzione per validare il file
  const validateFile = (file: File): { isValid: boolean; errorMessage?: string } => {
    // Validazione estensione file
    const allowedExtensions = ['jpeg', 'jpe', 'jpg', 'png', 'tiff', 'tif', 'pdf'];
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    
    if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
      return {
        isValid: false,
        errorMessage: t("errors.fileValidation.unsupportedFormat", { 
          formats: allowedExtensions.join(', ').toUpperCase() 
        }) as string
      };
    }

    // Validazione dimensione file (20MB = 20 * 1024 * 1024 bytes)
    const maxSizeInBytes = 20 * 1024 * 1024;
    if (file.size > maxSizeInBytes) {
      return {
        isValid: false,
        errorMessage: t("errors.fileValidation.fileTooLarge", { 
          maxSize: "20MB" 
        }) as string
      };
    }

    return { isValid: true };
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Validazione del file
      const validation = validateFile(file);
      if (!validation.isValid) {
        toast.error(validation.errorMessage || t("errors.fileValidation.invalidFile"));
        // Reset del file input
        if (e.target) {
          e.target.value = '';
        }
        return;
      }

      const currentTimestamp =
        new Date().toISOString().replace("T", " ").split(".")[0] + ".000";
      
      setSelectedFile(file);
      setDocName(file.name);
      setTimestamp(currentTimestamp);

      // Traccia l'inizio del flusso di upload del documento
      trackDocumentUploadFlow.startDocumentUpload(file.name, file.size, file.type, {
        action: action,
        hasGridDocumentData: !!gridDocumentData,
        selectedOption: gridDocumentData?.loadingTmsLocation || "N/A", // Assuming loadingTmsLocation is the selected option for now
        tripId: tripDetails?.tmsObjectNumber,
        userEmail: user?.email,
        source: 'grid'
      });

      // Traccia l'interazione con il file input
      trackInteraction('file_input', 'document_upload_grid', 'file_selected', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type
      });
    }
  };

  const handleDeleteFile = () => {
    if (selectedFile) {
      // Traccia l'interazione di eliminazione file
      trackInteraction('button', 'delete_file_grid', 'click', {
        fileName: selectedFile.name
      });
    }
    setSelectedFile(null);
  };

  const extractTransportOrderKey = (transportOrderTMSKey: string): string => {
    if (!transportOrderTMSKey || transportOrderTMSKey === 'N/A' || transportOrderTMSKey === 'undefined') {
      return 'N/A';
    }
    const parts = transportOrderTMSKey.split("_");
    return parts[parts.length - 1];
  };

  const generateStringsForGrid = () => {
    const results: { concatenated: string; orderKey: string }[] = [];
    
    if (!gridDocumentData) {
      return results;
    }
    
    const transportOrderKey = extractTransportOrderKey(
      gridDocumentData.transportOrderTMSKey || 'N/A'
    );
    
    // Crea la combinazione per questo specifico documento della grid
    // Usa direttamente i dati dal gridDocumentData
    const concatenated = `${gridDocumentData.loadingTmsLocation}_${gridDocumentData.unloadingTmsLocation}`;
    
    results.push({
      concatenated: concatenated,
      orderKey: transportOrderKey,
    });
    
    return results;
  };

  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      
      // Validazione del file
      const validation = validateFile(file);
      if (!validation.isValid) {
        toast.error(validation.errorMessage || t("errors.fileValidation.invalidFile"));
        return;
      }

      const currentTimestamp =
        new Date().toISOString().replace("T", " ").split(".")[0] + ".000";
      
      setSelectedFile(file);
      setDocName(file.name);
      setTimestamp(currentTimestamp);

      // Traccia l'inizio del flusso di upload del documento via drag & drop
      trackDocumentUploadFlow.startDocumentUpload(file.name, file.size, file.type, {
        action: action,
        hasGridDocumentData: !!gridDocumentData,
        selectedOption: gridDocumentData?.loadingTmsLocation || "N/A", // Assuming loadingTmsLocation is the selected option for now
        tripId: tripDetails?.tmsObjectNumber,
        userEmail: user?.email,
        uploadMethod: 'drag_drop',
        source: 'grid'
      });

      // Traccia l'interazione con il drag & drop
      trackInteraction('drag_drop', 'document_upload_grid', 'file_dropped', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type
      });
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      return;
    }
    if (!gridDocumentData) {
      return;
    }
    
    // Log unico per il debug dell'upload dalla grid
    console.log('[DEBUG GRID] Upload Process:', {
      // File info
      fileInfo: {
        fileName: selectedFile.name,
        fileSize: selectedFile.size,
        fileType: selectedFile.type
      },
      
      // Grid document data
      gridDocumentData,
      
      // Concatenated string that will be used
      concatenatedString: `${gridDocumentData.loadingTmsLocation}_${gridDocumentData.unloadingTmsLocation}`,
      
      // Action context
      action,
      
      // Trip info
      tripId: tripDetails?.tmsObjectNumber,
      userEmail: user?.email,
      
      // Source
      source: 'grid_upload_process'
    });
    
    // Traccia l'inizio dell'operazione di upload
    appInsights.trackEvent({
      name: 'SidebarAddDocumentGrid_Upload_Started',
      properties: {
        fileName: selectedFile.name,
        fileSize: selectedFile.size,
        fileType: selectedFile.type,
        action: action,
        hasGridDocumentData: !!gridDocumentData,
        selectedOption: gridDocumentData?.loadingTmsLocation || "N/A",
        source: 'grid'
      }
    });

    // Traccia il passaggio di processing del documento
    trackDocumentUploadFlow.trackDocumentProcessing('upload_processing_grid', {
      fileName: selectedFile.name,
      action: action
    });
    
    try {
      const response = await uploadPOD(selectedFile);
      
      if (response && Array.isArray(response) && response.length > 0) {
        const tmsObjectNumber = (gridDocumentData?.tmsObjectNumber || tripDetails?.tmsObjectNumber);
        const userMail = user ? user.email : "-";
        
        // Per PKL (action = true), non serve selezionare un punto di carico
        let concatenatedStrings;
        if (action) {
          concatenatedStrings = [{ concatenated: "PKL_GENERAL", orderKey: "PKL" }];
        } else {
          // Per la grid, usa sempre i dati dal gridDocumentData
          concatenatedStrings = [{
            concatenated: `${gridDocumentData.loadingTmsLocation}_${gridDocumentData.unloadingTmsLocation}`,
            orderKey: extractTransportOrderKey(gridDocumentData.transportOrderTMSKey || 'N/A'),
          }];
        }
        
        const selectedStop =
          concatenatedStrings[0]
            ? concatenatedStrings[0]
            : null;
        
        // Per PKL, non serve controllare selectedStop perché è sempre presente
        if (!action && !selectedStop) {
          return;
        }

        // Ottieni i dati di navigazione
        const navigationData = getNavigationDataAsString();
        
        const dynamicPayload: SendTripDocumentRequest = !action
        ? {
            ...PODPayloadBufferIds,
            document: {
              bufferIds: response,
            },
            fields: [
              ...PODPayloadBufferIds.fields.filter(
                (field) =>
                  ![
                    "TESTO227_131",
                    "TESTO246_136",
                    "DocName",
                    "Originale",
                    "TESTO265_131",
                    "DATA224_131",
                    "TESTO223_131",
                  ].includes(field.name)
              ),
              {
                value: tmsObjectNumber || 0,
                name: "TESTO227_131",
                externalId: "COD_VIAGGIO",
                className: "AdditionalFieldStringDTO",
              },
              {
                value:
                  concatenatedStrings[0]
                    .concatenated,
                name: "TESTO246_136",
                externalId: "MITTENTI_DESTINATARI",
                className: "AdditionalFieldStringDTO",
              },
              {
                value: docName,
                name: "DocName",
                className: "SubjectFieldDTO",
              },
              {
                value: selectedFile.name,
                name: "Originale",
                className: "OriginalFieldDTO",
              },
              {
                value: selectedStop?.orderKey || "N/A",
                name: "TESTO265_131",
                externalId: "COD_ORDINE",
                className: "AdditionalFieldStringDTO",
              },
              {
                value: timestamp,
                name: "DATA224_131",
                externalId: "DATAORAFOTO",
                className: "AdditionalFieldDateTimeDTO",
              },
              {
                value: userMail,
                name: "TESTO223_131",
                externalId: "ACCOUNT",
                className: "AdditionalFieldStringDTO",
              },
            ],
          }
        : {
          ...PODPayloadBufferIds,
          document: {
            bufferIds: response,
          },
          fields: [
            ...PODPayloadBufferIds.fields.filter(
              (field) =>
                ![
                  "TESTO227_131",
                  "TESTO246_136",
                  "DocName",
                  "Originale",
                  "TESTO265_131",
                  "DATA224_131",
                  "TESTO223_131",
                  "DocumentType",
                  "COMBO221_131",
                  "NUMERIC60_8",
                  "Stato",
                ].includes(field.name)
            ),
            {
              value: tmsObjectNumber || 0,
              name: "TESTO227_131",
              externalId: "COD_VIAGGIO",
              className: "AdditionalFieldStringDTO",
            },
            {
              value:
                concatenatedStrings[0]
                  .concatenated,
              name: "TESTO246_136",
              externalId: "MITTENTI_DESTINATARI",
              className: "AdditionalFieldStringDTO",
            },
            {
              value: docName,
              name: "DocName",
              className: "SubjectFieldDTO",
            },
            {
              value: selectedFile.name,
              name: "Originale",
              className: "OriginalFieldDTO",
            },
            {
              value: selectedStop?.orderKey || "N/A",
              name: "TESTO265_131",
              externalId: "COD_ORDINE",
              className: "AdditionalFieldStringDTO",
            },
            {
              value: timestamp,
              name: "DATA224_131",
              externalId: "DATAORAFOTO",
              className: "AdditionalFieldDateTimeDTO",
            },
            {
              value: userMail,
              name: "TESTO223_131",
              externalId: "ACCOUNT",
              className: "AdditionalFieldStringDTO",
            },
            {
              value: 357,
              name: "DocumentType",
              className: "DocumentTypeFieldDTO",
            },
            {
              value: "PKL",
              name: "COMBO221_131",
              externalId: "TIPO_DOCUMENTO",
              className: "AdditionalFieldComboDTO",
            },
            {
              value: "Valid",
              name: "Stato",
              className: "StateFieldDTO",
            },
          ],
        };

        // Traccia il passaggio di invio del documento
        trackDocumentUploadFlow.trackDocumentProcessing('document_sending_grid', {
          fileName: selectedFile.name,
          action: action,
          payloadSize: JSON.stringify(dynamicPayload).length
        });

        // Crea l'oggetto JSON con tutti i dati di navigazione visibili nel FAB
        const navigationInsightData = {
          sessione: sessionId,
          utente: userMail,
          passiTotali: navigationSteps.length,
          passi: navigationSteps.map((step: any, index: number) => ({
            numero: index + 1,
            nomePagina: getPageName(step.path),
            timestamp: formatTime(step.timestamp),
            path: step.path,
            parametri: step.searchParams || null,
            hash: step.hash || null,
            durataDalPassoPrecedente: step.duration ? formatDuration(step.duration) : null,
            tempoSullaPagina: step.timeOnPage && step.timeOnPage > 0 ? formatDuration(step.timeOnPage) : null,
            provenienza: step.referrer || null
          })),
          statistiche: {
            durataTotaleSessione: navigationSteps.length > 1 ? 
              formatDuration(navigationSteps[navigationSteps.length - 1].timestamp.getTime() - navigationSteps[0].timestamp.getTime()) : 
              '0s',
            pagineUniche: Array.from(new Set(navigationSteps.map((step: any) => step.path))).length
          }
        };

        // Insight per tracciare il payload completo della chiamata
        appInsights.trackEvent({
          name: 'SidebarAddDocumentGrid_SendTripDocument_Payload',
          properties: {
            fileName: selectedFile.name,
            action: action,
            tmsObjectNumber: tmsObjectNumber,
            userEmail: userMail,
            payload: JSON.stringify(dynamicPayload),
            timestamp: timestamp,
            docName: docName,
            concatenatedString: concatenatedStrings[0].concatenated,
            navigationInsightData: JSON.stringify(navigationInsightData, null, 2),
            tripDetailsResponse: tripDetails,
            gridDocumentData: gridDocumentData,
            tripId: tripIdFromUrl,
            source: 'grid'
          }
        });
        
        try {
          const sendTripResponse = await sendTripDocument(dynamicPayload);
        } catch (sendError) {
          throw sendError;
        }

        // Chiama la callback per fare il fetch dei documenti aggiornati
        onDocumentUploaded?.();

        // Traccia il completamento del flusso di upload
        trackDocumentUploadFlow.completeDocumentUpload(undefined, undefined, {
          fileName: selectedFile.name,
          action: action,
          tripId: tmsObjectNumber,
          userEmail: userMail,
          success: true,
          source: 'grid'
        });

        toast.success("Documento inviato con successo");
      } else {
        // Traccia l'errore dell'upload
        appInsights.trackEvent({
          name: 'SidebarAddDocumentGrid_Upload_Error',
          properties: {
            fileName: selectedFile.name,
            errorType: 'InvalidResponse',
            response: response,
            source: 'grid'
          }
        });

        // Traccia il fallimento del flusso di upload
        trackDocumentUploadFlow.failDocumentUpload('InvalidResponse', {
          fileName: selectedFile.name,
          action: action,
          response: response,
          source: 'grid'
        });
        
        toast.error("Errore durante l'invio del documento");
      }
    } catch (error) {
      // Traccia l'errore generale
      appInsights.trackEvent({
        name: 'SidebarAddDocumentGrid_Upload_Exception',
        properties: {
          fileName: selectedFile.name,
          errorMessage: error instanceof Error ? error.message : 'Unknown error',
          action: action,
          source: 'grid'
        }
      });

      // Traccia il fallimento del flusso di upload
      trackDocumentUploadFlow.failDocumentUpload(
        error instanceof Error ? error.message : 'Unknown error',
        {
          fileName: selectedFile.name,
          action: action,
          source: 'grid'
        }
      );
      
      toast.error("Errore durante il caricamento del file");
    } finally {
      callbackCloseSidebar(true);
      setSelectedFile(null);
    }
  };

  const onButtonClick = () => {
    inputRef.current?.click();
    
    // Traccia l'interazione con il pulsante di upload
    trackInteraction('button', 'upload_button_grid', 'click', {
      action: action
    });
  };

  return (
    <aside
      className={`flex flex-col transition z-[9999] fixed right-0 top-0 h-[100dvh] w-screen md:w-[520px] bg-white overflow-auto hide-scrollbar ${
        isOpen ? "translate-x-0 sidebar-shadow" : "translate-x-[100%]"
      }`}
    >
      {/* Header */}
      <div className="shrink-0 h-[88px] px-[30px] pt-[32px] pb-[24px] border-b flex justify-between items-center">
        <div className="text-lg font-bold">
          {t("common.sidebars.addContractDocument.heading")}
        </div>
        <div
          onClick={() => callbackCloseSidebar(false)}
          className="cursor-pointer"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="w-8 h-8"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </div>
      </div>

      {/* Main Content */}
      <div className="grow overflow-auto px-[30px] py-8">
        <input
          ref={inputRef}
          type="file"
          id="input-file-upload-grid"
          onChange={handleFileChange}
          style={{ display: "none" }}
        />

        {!selectedFile && (
          <>
            <Box sx={docSide.box}>
              <div
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                style={{
             
                  width:'100%',
                }}
                className={`flex justify-center  ${
                  isDragging ? "border-dashed border-2 border-blue-500 rounded-lg m-1" : ""
                }`}
              >
   
    <button onClick={onButtonClick} className="upload-button" style={{ padding: "68px 12px",}}>
                  {t("common.sidebars.addContractDocument.dragNDropText")}
                </button>
              </div>
            </Box>
            {currentPath.includes("/tripdetails") &&
            !currentPath.includes("/documents") && !action ? (
              ""
            ) : !action ? (
              <FormControl fullWidth sx={{ marginTop: 2 }}>
                <Typography
                  id="select-loading-point-label-grid"
                  sx={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    color: "primary.main",
                    marginTop: "32px",
                    marginBottom: "4px",
                    textTransform: "uppercase",
                  }}
                >
                  {t("screens.trips.timeline.loadingPoint")}:
                </Typography>
                <Typography
                  sx={{
                    padding: '8px', 
                    borderRadius: '4px', 
                    border: '1px solid #ccc',
                    backgroundColor: '#f5f5f5',
                    color: '#666',
                    fontSize: '14px',
                    minHeight: '40px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  {gridDocumentData?.loadingPoint || "N/A"}
                </Typography>

                {!gridDocumentData && (
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "warning.main",
                      marginTop: "4px",
                      fontStyle: "italic"
                    }}
                  >
                    {t('screens.trips.timeline.noLoadingPoints') || "Nessun punto di carico disponibile"}
                  </Typography>
                )}
              </FormControl>
            ) : null}
          </>
        )}

        {selectedFile && (
          <Box sx={docSide.box1}>
            <Typography sx={docSide.title}>
              {t("screens.trips.tripDocuments.document")}:
            </Typography>
            <Box sx={docSide.box2}>
              <div className="mr-2">{selectedFile.name}</div>
              <div
                onClick={handleDeleteFile}
                className="cursor-pointer"
                title="Delete"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6 7H5V20C5 20.5304 5.21071 21.0391 5.58579 21.4142C5.96086 21.7893 6.46957 22 7 22H17C17.5304 22 18.0391 21.7893 18.4142 21.4142C18.7893 21.0391 19 20.5304 19 20V7H6ZM10 19H8V10H10V19ZM16 19H14V10H16V19ZM16.618 4L15 2H9L7.382 4H3V6H21V4H16.618Z"
                    className="fill-red"
                  />
                </svg>
              </div>
            </Box>
            <Box sx={docSide.box3}>
              <Typography sx={docSide.description}>
                {t("screens.trips.tripDocuments.description")}
              </Typography>
              <TextField
                variant="outlined"
                fullWidth
                value={docName}
                onChange={(e) => setDocName(e.target.value)}
              />
            </Box>
          </Box>
        )}
      </div>
      <Box sx={docSide.box4}></Box>
      <div className="shrink-0 py-6 px-[30px] text-right border-t">
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={isDocumentLoading}
          sx={docSide.button}
        >
          {t("common.sidebars.saveButtonText")}
        </Button>
      </div>
    </aside>
  );
};

export default SidebarAddDocumentGrid; 